import type { GridSelection } from "@glideapps/glide-data-grid";
import {
  getCellKind,
  getRawInputKind,
} from "@/payroll/ai-script/cell-kind-utils";
import type {
  AiSpreadsheetCellKind,
  AiSpreadsheetValueType,
  IAiSpreadsheetCell,
  IAiSpreadsheetPasteEdit,
  IManualRowInput,
} from "@/payroll/ai-script/types";
import {
  AI_SPREADSHEET_CELL_KIND,
  isAiSpreadsheetNumericValueType,
} from "@/payroll/ai-script/types";
import { getInCellEditValue, isCellEditable } from "./adapter";
import { getSelectedCellCoordinates } from "./cell-selection";
import { translateFormulaReferences } from "./formula-reference-utils";
import { partitionPastedRows } from "./manual-row-utils";
import { normalizeSingleLineCellText } from "./utils";
import type {
  WorkbookCopiedCell,
  WorkbookCopiedRange,
} from "./workbook-clipboard";

// A per-cell grid of source value types read from the clipboard's text/html
// (Excel / Google Sheets). null = the source is text/unknown, so the target
// cell keeps its own type.
export type ClipboardCellTypes = (AiSpreadsheetValueType | null)[][];

const EXCEL_DATE_DISPLAY =
  /^(?:\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}-\d{1,2}-\d{1,2})(?:\s+\d{1,2}:\d{2}(?::\d{2})?\s*(?:am|pm)?)?$/i;

// Read the clipboard's text/html table (Excel or Google Sheets) into a per-cell
// type grid, so a paste can type cells from their SOURCE format. Best-effort:
// walk the table with the DOM (robust to the varied, sometimes-messy markup
// Excel/Sheets/browsers emit), then classify each cell with the pure, unit-
// tested classifyClipboardCellType. Returns null when there's no parseable table
// or no typed cell (plain-text paste), so the caller leaves cells' types alone.
export const parseClipboardCellTypes = (
  html: string,
): ClipboardCellTypes | null => {
  if (typeof DOMParser === "undefined") return null;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const table = doc.querySelector("table");
  if (!table) return null;

  const grid = Array.from(table.querySelectorAll("tr")).map((tr) =>
    Array.from(tr.querySelectorAll("td, th")).map((el) => {
      // Both apps right-align numerics — Excel via the `align` attribute, Sheets
      // via a `text-align` style; Excel Windows also tags `x:num`. Currency and
      // percent are read off the display text regardless of this signal.
      const numericSignal =
        (el as HTMLElement).getAttribute("align")?.toLowerCase() === "right" ||
        /right/i.test((el as HTMLTableCellElement).style?.textAlign ?? "") ||
        el.hasAttribute("x:num");
      return classifyClipboardCellType(el.textContent ?? "", numericSignal);
    }),
  );

  return grid.some((row) => row.some((type) => type !== null)) ? grid : null;
};

// Classify one clipboard cell's source type from its display text plus whether
// the source marks it numeric (right-aligned / x:num). Currency ($) and percent
// (%) are read straight off the display markers — a text cell wouldn't render
// those; a plain number additionally requires the numeric signal so a text-
// formatted numeric id isn't silently converted. Non-numeric → null (target cell
// keeps its own type). Pure + unit-tested (the DOM walk above is thin glue).
export const classifyClipboardCellType = (
  rawText: string,
  numericSignal: boolean,
): AiSpreadsheetValueType | null => {
  const text = rawText.replace(/\s+/g, " ").trim();
  if (!text) return null;

  // Excel stores dates as numbers but places the formatted date in the HTML
  // cell text. Require its numeric signal so date-like text remains text.
  if (numericSignal && EXCEL_DATE_DISPLAY.test(text)) return "date";

  // The stripped display must actually be a number (guards against text like an
  // id or a note) — allow thousands separators, a currency symbol, %, sign, and
  // parens for negatives.
  const numericBody = text.replace(/[\s,$£€%()+-]/g, "");
  if (!/^\d*\.?\d+$/.test(numericBody)) return null;

  if (/%\s*$/.test(text)) return "percent";
  if (/[$£€]/.test(text)) return "currency";
  return numericSignal ? "number" : null;
};

interface BuildBulkPastePayloadParams {
  values: readonly (readonly string[])[];
  startRow: number;
  startCol: number;
  maxContentRow: number;
  getCellRecord: (
    colIndex: number,
    rowIndex: number,
  ) => IAiSpreadsheetCell | undefined;
  // Per-cell source types from the clipboard HTML, aligned to `values`. When a
  // cell has a type here, the paste sends it so the server types the value
  // accordingly; otherwise the edit omits value_type (cell keeps its own type).
  cellTypes?: ClipboardCellTypes;
  copiedRange?: WorkbookCopiedRange;
}

export interface BulkPastePayload {
  edits: IAiSpreadsheetPasteEdit[];
  rows: IManualRowInput[];
}

const pastedValuesMatchCopiedRange = (
  values: readonly (readonly string[])[],
  copiedRange: WorkbookCopiedRange | undefined,
): copiedRange is WorkbookCopiedRange =>
  !!copiedRange &&
  values.length === copiedRange.values.length &&
  values.every(
    (row, rowIndex) =>
      row.length === copiedRange.values[rowIndex]?.length &&
      row.every(
        (value, colIndex) =>
          String(value ?? "") === copiedRange.values[rowIndex][colIndex],
      ),
  );

const getFiniteNumericString = (value: unknown) => {
  if (typeof value !== "number" && typeof value !== "string") return null;

  const normalizedValue = String(value).trim();
  return normalizedValue !== "" && Number.isFinite(Number(normalizedValue))
    ? normalizedValue
    : null;
};

const getInternallyCopiedNumericValue = (
  cell: WorkbookCopiedCell | null | undefined,
) => {
  if (
    !cell ||
    cell.kind !== AI_SPREADSHEET_CELL_KIND.value ||
    !isAiSpreadsheetNumericValueType(cell.valueType)
  ) {
    return null;
  }

  return (
    getFiniteNumericString(cell.rawInput) ??
    getFiniteNumericString(cell.computedValue)
  );
};

const translateInternallyCopiedValues = ({
  values,
  copiedRange,
  startRow,
  startCol,
  maxContentRow,
  getCellRecord,
}: Pick<
  BuildBulkPastePayloadParams,
  | "values"
  | "copiedRange"
  | "startRow"
  | "startCol"
  | "maxContentRow"
  | "getCellRecord"
>): {
  values: readonly (readonly string[])[];
  isInternalCopy: boolean;
} => {
  if (!pastedValuesMatchCopiedRange(values, copiedRange)) {
    return { values, isInternalCopy: false };
  }
  // Manual rows are appended directly after the current content. The grid keeps
  // a few selectable blank rows beneath that point, so a paste can target (for
  // example) row 44 while the new row actually lands at row 39. Translate from
  // the persisted destination, not the placeholder coordinate the user clicked.
  const destinationStartRow = Math.min(startRow, maxContentRow + 1);

  return {
    isInternalCopy: true,
    values: values.map((row, rowIndex) =>
      row.map((value, colIndex) => {
        const copiedCell = copiedRange.cells?.[rowIndex]?.[colIndex];
        const rawInput = copiedCell?.rawInput ?? String(value ?? "");
        const destination = getCellRecord(
          startCol - 1 + colIndex,
          startRow - 1 + rowIndex,
        );
        const sourceRow =
          copiedCell?.sourceRow ?? copiedRange.startRow + rowIndex;
        const sourceCol =
          copiedCell?.sourceCol ?? copiedRange.startCol + colIndex;
        const destinationRow =
          destination?.row ?? destinationStartRow + rowIndex;
        const destinationCol = destination?.col ?? startCol + colIndex;

        return copiedCell?.kind === AI_SPREADSHEET_CELL_KIND.formula ||
          (!copiedRange.cells && getRawInputKind(rawInput) === "formula")
          ? translateFormulaReferences(
              rawInput,
              destinationRow - sourceRow,
              destinationCol - sourceCol,
            )
          : (getInternallyCopiedNumericValue(copiedCell) ??
              String(value ?? ""));
      }),
    ),
  };
};

// Preserve formulas exactly; their string literals may contain intentional
// line breaks that should not be rewritten during paste.
const normalizePastedRawInput = (
  rawInput: string,
  kind: AiSpreadsheetCellKind,
  record: IAiSpreadsheetCell,
) =>
  kind === AI_SPREADSHEET_CELL_KIND.formula
    ? rawInput
    : normalizeSingleLineCellText(rawInput, record);

export const buildBulkPastePayload = ({
  values,
  startRow,
  startCol,
  maxContentRow,
  getCellRecord,
  cellTypes,
  copiedRange,
}: BuildBulkPastePayloadParams): BulkPastePayload | null => {
  const translatedPaste = translateInternallyCopiedValues({
    values,
    copiedRange,
    startRow,
    startCol,
    maxContentRow,
    getCellRecord,
  });
  const effectiveCellTypes =
    translatedPaste.isInternalCopy && copiedRange?.cells
      ? copiedRange.cells.map((row) =>
          row.map((cell) => cell?.valueType ?? null),
        )
      : cellTypes;
  const cellKinds: readonly (readonly AiSpreadsheetCellKind[])[] =
    translatedPaste.values.map((row, rowIndex) =>
      row.map((value, colIndex) =>
        translatedPaste.isInternalCopy
          ? (copiedRange?.cells?.[rowIndex]?.[colIndex]?.kind ??
            getRawInputKind(String(value ?? "")))
          : AI_SPREADSHEET_CELL_KIND.value,
      ),
    );
  const { existingRows, newRows } = partitionPastedRows({
    values: translatedPaste.values,
    startRow,
    startCol,
    maxContentRow,
    cellKinds,
    cellValueTypes: effectiveCellTypes?.map((row) =>
      row.map((valueType) => valueType ?? undefined),
    ),
  });
  const edits: BulkPastePayload["edits"] = [];

  existingRows.forEach(({ gridRow, values: rowValues }) => {
    const typeRow = effectiveCellTypes?.[gridRow - startRow];
    rowValues.forEach((rawValue, colOffset) => {
      const record = getCellRecord(startCol - 1 + colOffset, gridRow - 1);
      if (!record || !isCellEditable(record)) return;

      const existingKind = getCellKind(record);
      const kind = cellKinds[gridRow - startRow]?.[colOffset] ?? existingKind;
      const rawInput = normalizePastedRawInput(
        String(rawValue ?? ""),
        kind,
        record,
      );
      if (rawInput === getInCellEditValue(record) && kind === existingKind) {
        return;
      }

      // Type the value ONLY when the clipboard's source cell declares one
      // (Excel/Sheets numeric) — never guessed from the raw string. Otherwise
      // omit value_type so the cell keeps its own type/format. Manual-row cells
      // are skipped: their row seed owns the type.
      const value_type = record.manual_row_id
        ? undefined
        : (typeRow?.[colOffset] ?? undefined);

      edits.push({
        cell_id: record.id,
        raw_input: rawInput,
        kind,
        ...(value_type ? { value_type } : {}),
      });
    });
  });

  if (edits.length === 0 && newRows.length === 0) return null;

  return { edits, rows: newRows };
};

interface BuildBulkClearPayloadParams {
  selection: GridSelection;
  // 1-based maxima of the content region (see getSheetContentBounds).
  maxContentRow: number;
  maxContentCol: number;
  getCellRecord: (
    colIndex: number,
    rowIndex: number,
  ) => IAiSpreadsheetCell | undefined;
}

// Build the bulk-paste payload that clears a selection (a Backspace/Delete over
// a range, whole row, or whole column): one empty-value edit per editable,
// non-empty existing cell, deduped across overlapping ranges. Read-only, blank,
// and already-empty cells are skipped. Returns null when nothing is clearable,
// so the caller can swallow the keystroke instead of firing an empty request.
export const buildBulkClearPayload = ({
  selection,
  maxContentRow,
  maxContentCol,
  getCellRecord,
}: BuildBulkClearPayloadParams): BulkPastePayload | null => {
  const edits: BulkPastePayload["edits"] = [];
  const seen = new Set<string>();

  getSelectedCellCoordinates(selection, maxContentRow, maxContentCol).forEach(
    ([col, row]) => {
      const record = getCellRecord(col, row);
      if (!record || !isCellEditable(record) || seen.has(record.id)) return;
      if (getInCellEditValue(record) === "") return;

      seen.add(record.id);
      edits.push({
        cell_id: record.id,
        raw_input: "",
        kind: AI_SPREADSHEET_CELL_KIND.value,
      });
    },
  );

  if (edits.length === 0) return null;

  return { edits, rows: [] };
};
